import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Dossier } from '@sc/data-access-models';
import { InsuranceCoverSandboxService } from '@sc/data-access-state-management';
import { PhoneNumberPipe } from '@sc/feature-contextual-display';
import { GrayBoxComponent, IconComponent } from '@sc/ui-components';
import { DeclarationConditions } from '@sc/utils';

@Component({
  selector: 'sc-side-summary',
  styleUrls: ['./side-summary.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GrayBoxComponent,
    NgClass,
    NgIf,
    IconComponent,
    MatTooltipModule,
    PhoneNumberPipe,
    AsyncPipe,
    TranslateModule,
  ],
  template: `
    <sc-gray-box>
      <div class="container">
        <h1 class="sc-design-title-h1">
          {{ 'SideSummary.information' | translate }}
        </h1>

        <div class="side-step">
          <div
            class="step-head"
            [ngClass]="{
              'glass-choice-unfilled': isGlassChoiceUnfilled()
            }"
          >
            <div class="oval">
              <sc-icon
                [class]="(isCovered$ | async) ? 'insured' : 'notInsured'"
                [name]="(isCovered$ | async) ? 'check' : 'XCircle'"
                *ngIf="isValidCustomerData"
              ></sc-icon>
            </div>
            <span class="sc-design-text-3">{{
              (isCovered$ | async)
                ? ('SideSummary.customerOrMember' | translate)
                : ('SideSummary.notInsured' | translate)
            }}</span>
          </div>

          <div
            *ngIf="isValidCustomerData && !isValidClaimData"
            class="step-details"
          >
            <div style="margin: 12px;">
              <span class="title row sc-design-text-3-bold"
                >{{ dossier?.customer?.firstname }}
                {{ dossier?.customer?.lastname }}</span
              ><span
                class="row mailTitle sc-design-text-3"
                [matTooltip]="dossier?.customer?.email"
                matTooltipClass="matTooltipClassCustom"
              >
                {{ dossier?.customer?.email }}
              </span>

              <span class="row sc-design-text-3">{{
                dossier?.customer?.phoneNumber?.number | phoneNumber | async
              }}</span>

              <span
                class="row sc-design-text-3"
                *ngIf="
                  dossier?.claim?.car?.manufacturer &&
                  dossier?.claim?.car?.version
                "
              >
                {{ getCarDisplayInfos() }}</span
              >
              <span class="row sc-design-text-3">{{
                dossier?.claim?.car?.licensePlateNumber
              }}</span>
            </div>
          </div>
          <div
            *ngIf="isValidCustomerData && !isValidClaimData"
            class="step-details"
          >
            <div style="margin: 12px;">
              <span
                class="row sc-design-text-3 insurerTitleRow"
                [innerHTML]="
                  'SideSummary.insurerTitle'
                    | translate
                      : {
                          insurerName: dossier?.policyInformation?.insuranceName
                        }
                "
              ></span>
              <span
                class="row sc-design-text-3"
                [innerHTML]="
                  'SideSummary.contractNumberTitle'
                    | translate
                      : {
                          contractNumber:
                            dossier?.policyInformation?.policyNumber
                        }
                "
              ></span>

              <ng-container *ngIf="!!(insuranceMessage$ | async)">
                <span
                  class="row insurance-text sc-design-text-3"
                  [innerHTML]="insuranceMessage$ | async"
                  [ngClass]="{
                    'glass-choice-unfilled': isGlassChoiceUnfilled()
                  }"
                ></span>
                <span
                  *ngIf="!!(coverMessage$ | async)"
                  class="row cover-text sc-design-text-3"
                  [innerHTML]="coverMessage$ | async"
                  [ngClass]="{
                    'glass-choice-unfilled': isGlassChoiceUnfilled()
                  }"
                ></span>
                <span
                  *ngIf="!!(coverRangeMessage$ | async)"
                  class="row cover-range-text sc-design-text-3"
                  [innerHTML]="coverRangeMessage$ | async"
                  [ngClass]="{
                    'glass-choice-unfilled': isGlassChoiceUnfilled()
                  }"
                ></span>
              </ng-container>
            </div>
          </div>
        </div>
      </div>
    </sc-gray-box>
  `,
})
export class SideSummaryComponent {
  @Input() dossier: Dossier;
  @Input() isValidCustomerData: boolean;
  @Input() isValidClaimData: boolean;

  fullName: string;
  email: string;
  insurerName: string;
  phoneNumber: string;
  carLicensePlate: string;
  carManufacturer: string;
  carModel: string;

  isCovered$ = this.insuranceCoverService.getIsCovered();
  insuranceMessage$ = this.insuranceCoverService.getInsuredMessage();
  coverMessage$ = this.insuranceCoverService.getCoverMessage();
  coverRangeMessage$ = this.insuranceCoverService.getCoverRangeMessage();

  constructor(
    private readonly insuranceCoverService: InsuranceCoverSandboxService,
    public declarationConditions: DeclarationConditions
  ) {}

  getCarDisplayInfos() {
    let carInfos = '';
    carInfos += this.dossier?.claim?.car?.manufacturer
      ? this.dossier?.claim?.car?.manufacturer
      : '';

    carInfos += this.dossier?.claim?.car?.model
      ? ' ' + this.dossier?.claim?.car?.model
      : '';
    carInfos += this.dossier?.claim?.car?.characteristics?.numberOfDoors
      ? ', ' + this.dossier?.claim?.car?.characteristics?.numberOfDoors + 'P'
      : '';
    return carInfos;
  }

  isGlassChoiceUnfilled() {
    return !this.declarationConditions.isGlassChoiceFilled(this.dossier);
  }
}
